/* -----------------Globals--------------- */
import { useCallback, useState } from "react";

import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useExamContext } from "@features/exam/contexts/ExamContext";
/* -----------------Helpers & Hooks--------------- */
import { useAppStore } from "@hooks/useAppStore";

/* -----------------Types--------------- */
import type { Stat } from "components/RowStats";

import { isScreenshotMode } from "@constants/common";
import { mockedProfileDetails } from "@constants/mocked-data";
import { getParentMetadata } from "@helpers/content/metadata";
import useAuthUser from "@hooks/useAuthUser";
import type { LeaderboardRecord } from "@memorang/types";
import type { MixedItemDist } from "@memorang/types";
import { getMixedItemDistribution } from "api/queries";
import { useFocusEffect } from "expo-router";
import { fetchUserRank } from "../api/queries";

/* -----------------Child components--------------- */
import { getHeaderStats, getStatsSectionData } from "../helpers/profile-data";

type ProfileDetails = {
	headerStats: Stat[];
	statsTabData: ReturnType<typeof getStatsSectionData>;
};

type SettledResult<T> =
	| {
			status: "fulfilled";
			value: T;
	  }
	| {
			status: "rejected";
			reason: unknown;
	  };

const emptyLeaderboardRecord: LeaderboardRecord = {
	xp: 0,
	rank: 0,
	username: "",
	avatar: "",
	level: 0,
};

const getSettledValue = <T>(
	result: SettledResult<unknown> | undefined,
	fallback: T,
): T => {
	if (!result || result.status !== "fulfilled" || result.value == null) {
		return fallback;
	}

	return result.value as T;
};

const toSettledResult = async <T>(
	promise: Promise<T>,
): Promise<SettledResult<T>> => {
	try {
		const value = await promise;
		return { status: "fulfilled", value };
	} catch (reason) {
		return { status: "rejected", reason };
	}
};

const allSettled = (promises: Promise<unknown>[]) => {
	return Promise.all(promises.map((promise) => toSettledResult(promise)));
};
const useProfileDetails = () => {
	const [profileDetails, setProfileDetails] = useState<ProfileDetails>();
	const {
		currentExamContext: { examId, projectId, organizationId },
	} = useExamContext();
	const { viewerId } = useAuthContext();
	const exam = useAppStore((store) => store.exam);

	const { userPublicMetadataId } = useAuthUser();

	const { currentExamContext } = useExamContext();
	const { examDate } = currentExamContext;
	const setUpProfileDetails = useCallback(async () => {
		const [
			allTimeLeaderboardResult,
			monthLeaderboardResult,
			weekLeaderboardResult,
			contentsResult,
		] = await allSettled([
			fetchUserRank({
				periodType: "all_time",
				projectId,
				organizationId,
				userPublicMetadataId: userPublicMetadataId!,
			}),
			fetchUserRank({
				periodType: "month",
				projectId,
				organizationId,
				userPublicMetadataId: userPublicMetadataId!,
			}),
			fetchUserRank({
				periodType: "week",
				projectId,
				organizationId,
				userPublicMetadataId: userPublicMetadataId!,
			}),
			...(examId
				? [
						getMixedItemDistribution({
							userPublicMetadataId: userPublicMetadataId!,
							examId,
							action: "COMMON_DISTRIBUTION",
						}),
					]
				: []),
		]);

		const allTimeLeaderboard = getSettledValue<LeaderboardRecord>(
			allTimeLeaderboardResult,
			emptyLeaderboardRecord,
		);
		const monthLeaderboard = getSettledValue<LeaderboardRecord>(
			monthLeaderboardResult,
			emptyLeaderboardRecord,
		);
		const weekLeaderboard = getSettledValue<LeaderboardRecord>(
			weekLeaderboardResult,
			emptyLeaderboardRecord,
		);
		const mixedItemDistResponse = getSettledValue<MixedItemDist | null>(
			contentsResult,
			null,
		);
		const normalizedContents = Array.isArray(mixedItemDistResponse?.contents)
			? mixedItemDistResponse.contents
			: [];
		const { distribution } = getParentMetadata(normalizedContents);

		const low = distribution.low || 0;
		const high = distribution.high || 0;
		const medium = distribution.medium || 0;
		const numItemsAnswered = low + high + medium;
		const accuracy = numItemsAnswered
			? Math.round(((high + medium) / numItemsAnswered) * 100)
			: 0;
		const level = allTimeLeaderboard.level;
		const headerStats = getHeaderStats({
			level,
			accuracy,
			answered: numItemsAnswered,
		});
		const statsTabData = getStatsSectionData({
			selectedBundleContext: currentExamContext,
			selectedDate: examDate!,
			allTimeLeaderboard,
			monthLeaderboard,
			weekLeaderboard,
			examEnabled: exam?.enabled,
		});
		const data = {
			headerStats,
			statsTabData: statsTabData,
		};
		setProfileDetails(data);
	}, [
		currentExamContext,
		exam?.enabled,
		examDate,
		examId,
		projectId,
		organizationId,
		userPublicMetadataId,
	]);

	useFocusEffect(
		useCallback(() => {
			if (viewerId && examId) {
				setUpProfileDetails();
			}
		}, [setUpProfileDetails, viewerId, examId]),
	);

	return {
		profileDetails: isScreenshotMode ? mockedProfileDetails : profileDetails,
		setUpProfileDetails,
	};
};

export default useProfileDetails;
